import { supabase } from "../../supabase/supabase";
import { ApiKey, CreatedApiKey } from "@/types/organization";

// Expiry choices for a new key; null = never expires
export const API_KEY_EXPIRY_OPTIONS: { days: number | null; label: string }[] = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 180, label: "180 days" },
  { days: 365, label: "1 year" },
  { days: null, label: "Never" },
];
export const DEFAULT_API_KEY_EXPIRY_DAYS = 90;

const LIST_COLUMNS =
  "id, organization_id, name, key_prefix, created_by, created_at, expires_at, revoked, revoked_at, revoked_by, last_used_at";

export const apiKeyService = {
  /**
   * Create an API key (owners and admins). The full key is in the result only.
   */
  async createApiKey(
    organizationId: string,
    name: string,
    expiresInDays: number | null = DEFAULT_API_KEY_EXPIRY_DAYS,
  ): Promise<CreatedApiKey> {
    const { data, error } = await supabase.functions.invoke("create_api_key", {
      body: { organization_id: organizationId, name, expiresInDays },
    });
    if (error) {
      // The function's own message (e.g. "Only owners and admins can create API keys")
      const message = await error.context?.json?.().then((b: any) => b?.error).catch(() => null);
      throw new Error(message || error.message);
    }
    return data;
  },

  /**
   * The organization's API keys, newest first (no hashes).
   */
  async getApiKeys(organizationId: string): Promise<ApiKey[]> {
    const { data, error } = await supabase
      .from("api_keys")
      .select(LIST_COLUMNS)
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching API keys:", error);
      return [];
    }
    return data || [];
  },

  /**
   * Revoke an API key (owners, admins and platform admins). Takes effect immediately.
   */
  async revokeApiKey(keyId: string): Promise<void> {
    const { data: userData } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("api_keys")
      .update({
        revoked: true,
        revoked_at: new Date().toISOString(),
        revoked_by: userData.user?.id ?? null,
      })
      .eq("id", keyId)
      .select("id");
    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error("Only owners and admins can revoke API keys");
    }
  },
};
